import { IconArrowUp } from "@/assets/icons/geist/IconArrowUp";
import { IconEdit } from "@/assets/icons/geist/IconEdit";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { Markdown } from "@/components/ai/chat/Markdown";
import { CodeContextPreview } from "@/components/ai/review/CodeContextPreview";
import type { ReviewComment, CachedCodeLine } from "@/components/ai/review/types";
import { Button } from "@/components/flexkit/Button";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { type FC, useState, useCallback } from "react";

export type ReviewCommentsSectionProps = {
    environmentId: string;
    comments: ReviewComment[];
    selectedCommentIds: Set<string>;
    onToggleSelect: (commentId: string) => void;
    onEditComment: (commentId: string, content: string) => void;
    onRemoveComment: (commentId: string) => void;
    onUpdateCodeContext: (commentId: string, hash: string, lines: CachedCodeLine[]) => void;
    onSubmitClick: () => void;
    onDismiss: () => void;
    submitButtonText: string;
    isSubmitting: boolean;
};

export const ReviewCommentsSection: FC<ReviewCommentsSectionProps> = ({
    environmentId,
    comments,
    selectedCommentIds,
    onToggleSelect,
    onEditComment,
    onRemoveComment,
    onUpdateCodeContext,
    onSubmitClick,
    onDismiss,
    submitButtonText,
    isSubmitting,
}) => {
    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <Text className="text-md font-medium">Review Comments</Text>
                <div className="flex items-center gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={onDismiss}
                        disabled={isSubmitting}
                        data-tracking-id="dismiss-review-comments"
                    >
                        Dismiss
                    </Button>
                    <Button
                        size="sm"
                        onClick={onSubmitClick}
                        disabled={isSubmitting || comments.length === 0}
                        LeadingIcon={IconArrowUp}
                        data-tracking-id="submit-review-comments"
                    >
                        {submitButtonText}
                    </Button>
                </div>
            </div>
            <div className="flex flex-col gap-3">
                {comments.map((comment) => (
                    <ReviewCommentCard
                        key={comment.id}
                        environmentId={environmentId}
                        comment={comment}
                        isSelected={selectedCommentIds.has(comment.id)}
                        onToggleSelect={() => onToggleSelect(comment.id)}
                        onEditComment={onEditComment}
                        onRemove={() => onRemoveComment(comment.id)}
                        onUpdateCodeContext={(hash, lines) => onUpdateCodeContext(comment.id, hash, lines)}
                    />
                ))}
            </div>
        </div>
    );
};

type ReviewCommentCardProps = {
    environmentId: string;
    comment: ReviewComment;
    isSelected: boolean;
    onToggleSelect: () => void;
    onEditComment: (commentId: string, content: string) => void;
    onRemove: () => void;
    onUpdateCodeContext: (hash: string, lines: CachedCodeLine[]) => void;
};

const ReviewCommentCard: FC<ReviewCommentCardProps> = ({
    environmentId,
    comment,
    isSelected,
    onToggleSelect,
    onEditComment,
    onRemove,
    onUpdateCodeContext,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState("");

    const handleStartEdit = useCallback(() => {
        setIsEditing(true);
        setEditContent(comment.content);
    }, [comment.content]);

    const handleSaveEdit = useCallback(() => {
        if (editContent.trim()) {
            onEditComment(comment.id, editContent.trim());
        }
        setIsEditing(false);
        setEditContent("");
    }, [comment.id, editContent, onEditComment]);

    const handleCancelEdit = useCallback(() => {
        setIsEditing(false);
        setEditContent("");
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSaveEdit();
        } else if (e.key === "Escape") {
            e.preventDefault();
            handleCancelEdit();
        }
    };

    return (
        <div
            id={`review-comment-${comment.id}`}
            className={cn(
                "rounded-lg border bg-surface-secondary",
                isSelected ? "border-content-primary" : "border-border-base",
            )}
        >
            {/* Code context preview with header */}
            <CodeContextPreview
                environmentId={environmentId}
                filePath={comment.filePath}
                startLine={comment.startLine}
                endLine={comment.endLine}
                originalContentHash={comment.contentHash}
                cachedCodeLines={comment.cachedCodeLines}
                onCodeContextComputed={onUpdateCodeContext}
                leadingElement={
                    <Checkbox
                        checked={isSelected}
                        onCheckedChange={onToggleSelect}
                        aria-label={`Select comment on ${comment.filePath}`}
                    />
                }
            >
                {!isEditing && (
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={handleStartEdit}
                            aria-label="Edit comment"
                            data-tracking-id="edit-review-comment"
                        >
                            <IconEdit size="sm" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-content-negative hover:text-content-negative"
                            onClick={onRemove}
                            aria-label="Remove comment"
                            data-tracking-id="remove-review-comment"
                        >
                            <IconTrash size="sm" />
                        </Button>
                    </div>
                )}
            </CodeContextPreview>

            {/* Comment content */}
            <div className="p-3">
                {isEditing ? (
                    <div className="flex flex-col gap-2">
                        <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="min-h-[80px] w-full resize-none rounded-md border border-border-base bg-surface-primary p-2 text-sm text-content-primary placeholder:text-content-muted focus:border-border-strong focus:outline-none"
                            placeholder="Enter your comment..."
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleCancelEdit}
                                data-tracking-id="cancel-edit-review-comment"
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleSaveEdit}
                                disabled={!editContent.trim()}
                                data-tracking-id="save-edit-review-comment"
                            >
                                Save
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="prose prose-sm max-w-none text-content-primary">
                        <Markdown content={comment.content} />
                    </div>
                )}
            </div>
        </div>
    );
};
